/**
 * The `Internal Audits` list as typed data (13-32, give-first, D-13-L-13): FIELDSET §2.6 (the columns), §3.6 (the
 * stages), §1.8's audit row (which verb sets which date) and the carry-in split (`carry_in_internal_audits.py`).
 *
 * Internal Audits is Oversight's list, not one of the five registers (FIELDSET §3.6), so it has its own small field
 * set here rather than a sixth `RecordRegister`: the register types, the lens, the lodge and Today stay untouched.
 * Its choices are read from 13-17's live snapshot (`liveColumns.json`, which carries this list since 13-32), never
 * retyped; `internalAudits.test.ts` checks every column against it the way `fieldset.test.ts` checks the registers.
 *
 * - Reference: `AUD-<SP Id>` (no calculated `Reference`, D-12.1-04-12); the sibling `ParentRef` is the same.
 * - No generic `Status`: `AuditStatus` (Centro-verbatim) is the lifecycle. A verb never writes `Completion Overdue`:
 *   it is a Centro state word the carry-in brings, and the dashboard derives "overdue" from `DueOn`.
 * - `CentroRef` is the carry-in's idempotent key: no verb writes it.
 */

import { LIVE_COLUMNS, liveColumnIn } from './live';
import { LOG_KIND_MAP } from './siblings';

/** FIELDSET §2.6 / §6.6: the live list and its sibling (12.1-11.1, 7 Oct 2026). */
export const INTERNAL_AUDITS = {
  listTitle: 'Internal Audits',
  listId: '1459a370-4ae0-4f9c-9d24-821ce5cc4db6',
  auditListTitle: 'Internal Audits Audit Log',
  auditListId: '751e1618-6274-449b-9d26-e806ddc9e991',
  prefix: 'AUD',
} as const;

/**
 * How a column is captured: the register kinds where they fit (`text`, `textarea`, `choice`, `day-new`, `upn`,
 * `number`, `attachments`), plus `link`: `FindingsLink`, a web address of 255 characters or fewer (§2.6).
 */
export type AuditFieldKind = 'text' | 'textarea' | 'choice' | 'day-new' | 'upn' | 'number' | 'link' | 'attachments';

export interface AuditFieldSpec {
  /** SharePoint internal name (= the display name, D-12.1-05-1). */
  readonly name: string;
  /** §2.6's display label. */
  readonly label: string;
  readonly kind: AuditFieldKind;
  /** Text kinds: the live `maxLength` (255), or 4,000 for a Note. */
  readonly cap?: number;
}

const TEXT = 255;
const NOTE = 4000;

/** FIELDSET §2.6, in its row order: `Title` + the 17 new columns + the built-in `Attachments`. */
export const AUDIT_FIELDS: readonly AuditFieldSpec[] = [
  { name: 'Title', label: 'Audit name', kind: 'text', cap: TEXT },
  { name: 'AuditTemplate', label: 'Audit template', kind: 'text', cap: TEXT },
  { name: 'TemplateVersion', label: 'Template version', kind: 'text', cap: TEXT },
  { name: 'RecommendedFrequency', label: 'Recommended frequency', kind: 'text', cap: TEXT },
  { name: 'BusinessUnit', label: 'Business unit audited', kind: 'choice' },
  { name: 'AuditKind', label: 'Internal audit or system review?', kind: 'choice' },
  { name: 'DueOn', label: 'Due on', kind: 'day-new' },
  { name: 'AuditorUPN', label: 'Assigned auditor', kind: 'upn', cap: TEXT },
  { name: 'AuditStatus', label: 'Audit status', kind: 'choice' },
  { name: 'AuditorIndependent', label: 'Auditor independent of the area?', kind: 'choice' },
  { name: 'CompletedOn', label: 'Completed on', kind: 'day-new' },
  { name: 'FindingsLink', label: 'Completed forms and evidence (link)', kind: 'link', cap: TEXT },
  { name: 'ScorePct', label: 'Score (%)', kind: 'number' },
  { name: 'Explanation', label: 'Explanation', kind: 'textarea', cap: NOTE },
  { name: 'Recommendations', label: 'Recommendations', kind: 'textarea', cap: NOTE },
  { name: 'ApprovedOn', label: 'Approved on', kind: 'day-new' },
  { name: 'ApprovedByUPN', label: 'Approved by', kind: 'upn', cap: TEXT },
  { name: 'CentroRef', label: 'Centro audit id (carried rows only)', kind: 'text', cap: TEXT },
  { name: 'Attachments', label: 'Working papers and evidence', kind: 'attachments' },
];

const BY_NAME: ReadonlyMap<string, AuditFieldSpec> = new Map(AUDIT_FIELDS.map((f) => [f.name, f]));

export function auditField(name: string): AuditFieldSpec | undefined {
  return BY_NAME.get(name);
}

/** The live choices of an Internal Audits choice column, in live order. Throws if it is not one. */
export function auditChoices(column: string): readonly string[] {
  const col = liveColumnIn(LIVE_COLUMNS, INTERNAL_AUDITS.listTitle, column);
  if (!col || col.type !== 'choice' || !col.choices) {
    throw new Error(`internalAudits: ${INTERNAL_AUDITS.listTitle}.${column} is not a live choice column`);
  }
  return col.choices;
}

/** The named values, each checked to be a live choice of `column` (a rule or a constant names them). */
export function auditChoice(column: string, ...values: string[]): readonly string[] {
  const live = auditChoices(column);
  for (const v of values) {
    if (!live.includes(v)) throw new Error(`internalAudits: ${JSON.stringify(v)} is not a live choice of ${INTERNAL_AUDITS.listTitle}.${column}`);
  }
  return values;
}

// Every column is live, and every choice column has live choices (fails at load, as 13-17's readers do).
for (const f of AUDIT_FIELDS) {
  if (!liveColumnIn(LIVE_COLUMNS, INTERNAL_AUDITS.listTitle, f.name)) throw new Error(`internalAudits: ${f.name} is not a live column`);
  if (f.kind === 'choice') auditChoices(f.name);
}

// ---------------------------------------------------------------------------
// The lifecycle words (AuditStatus, Centro-verbatim; FIELDSET §1.9)
// ---------------------------------------------------------------------------

export const AUDIT_STATUS = {
  awaiting: auditChoice('AuditStatus', 'Awaiting Completion')[0],
  overdue: auditChoice('AuditStatus', 'Completion Overdue')[0],
  inProgress: auditChoice('AuditStatus', 'In Progress')[0],
  completed: auditChoice('AuditStatus', 'Completed')[0],
  approved: auditChoice('AuditStatus', 'Approved')[0],
} as const;

/** An audit not yet completed: the three "to do" words (FIELDSET §6.6's `Scheduled` view filter). */
export const OPEN_AUDIT_STATUSES: readonly string[] = [AUDIT_STATUS.awaiting, AUDIT_STATUS.overdue, AUDIT_STATUS.inProgress];

// ---------------------------------------------------------------------------
// FIELDSET §3.6 (verbatim) and the verbs
// ---------------------------------------------------------------------------

/** FIELDSET §3.6's three Internal Audits stages, verbatim (10 / 4 / 6). */
export const AUDIT_STAGES = {
  'lodge-oversight': [
    'Title', 'AuditTemplate', 'TemplateVersion', 'RecommendedFrequency', 'BusinessUnit', 'AuditKind', 'DueOn',
    'AuditorUPN', 'AuditStatus', 'AuditorIndependent',
  ],
  investigation: ['AuditStatus', 'CompletedOn', 'FindingsLink', 'Attachments'],
  closure: ['AuditStatus', 'ScorePct', 'Explanation', 'Recommendations', 'ApprovedOn', 'ApprovedByUPN'],
} as const satisfies Record<string, readonly string[]>;

/**
 * The audit verbs (FIELDSET §1.8's audit row, the plan's three plus their parts):
 * - `schedule`: a new row (the lodge-oversight columns; `AuditStatus` = `Awaiting Completion`);
 * - `edit`: an open audit's schedule columns (a carried row: only `CARRY_IN.editable` and a defaulted person);
 * - `start`: `Awaiting Completion` / `Completion Overdue` → `In Progress` (complete's optional first step);
 * - `complete`: → `Completed`, with `CompletedOn` and `FindingsLink`;
 * - `approve`: `Completed` → `Approved`, with the score, explanation, recommendations, `ApprovedOn`, `ApprovedByUPN`;
 * - `followUp`: rows only, the §3.4.1 `Review` (follow-up verdict) and `AuditMeeting` rows.
 */
export type AuditVerb = 'schedule' | 'edit' | 'start' | 'complete' | 'approve' | 'followUp';

export const AUDIT_VERBS: readonly AuditVerb[] = ['schedule', 'edit', 'start', 'complete', 'approve', 'followUp'];

const SCHEDULE_INPUTS: readonly string[] = AUDIT_STAGES['lodge-oversight'].filter((c) => c !== 'AuditStatus');

/** What a person enters for each verb. */
export const AUDIT_VERB_INPUTS: Readonly<Record<AuditVerb, readonly string[]>> = {
  schedule: SCHEDULE_INPUTS,
  edit: SCHEDULE_INPUTS,
  start: [],
  complete: ['CompletedOn', 'FindingsLink'],
  approve: ['ScorePct', 'Explanation', 'Recommendations'],
  followUp: [],
};

/** Where a stamp comes from: a status word, Melbourne today (a `dateOnly` day), or the actor's UPN. */
export type AuditStamp = string;

/** The columns each verb sets itself (a typed value is refused `stamped-column`). */
export const AUDIT_VERB_STAMPS: Readonly<Record<AuditVerb, Readonly<Record<string, AuditStamp>>>> = {
  schedule: { AuditStatus: AUDIT_STATUS.awaiting },
  edit: {},
  start: { AuditStatus: AUDIT_STATUS.inProgress },
  complete: { AuditStatus: AUDIT_STATUS.completed },
  approve: { AuditStatus: AUDIT_STATUS.approved, ApprovedOn: 'today', ApprovedByUPN: 'actor' },
  followUp: {},
};

/**
 * The columns a verb may write: its inputs and its stamps. `edit` on a carried row may also correct a person column
 * the carry-in defaulted to the runner (`CARRY_IN.defaultable`, D-13-32-4); that exception is the verb builder's.
 */
export function auditVerbColumns(verb: AuditVerb): string[] {
  const extra = verb === 'edit' ? CARRY_IN.defaultable.filter((c) => !SCHEDULE_INPUTS.includes(c)) : [];
  return [...new Set([...AUDIT_VERB_INPUTS[verb], ...Object.keys(AUDIT_VERB_STAMPS[verb]), ...extra])];
}

/**
 * FIELDSET §2.6's `Required from stage` cells: "schedule (verb)" (TemplateVersion and RecommendedFrequency are
 * optional), the complete verb's two, and the approve verb's two (Recommendations is optional). `edit` keeps the
 * schedule ones: it refuses clearing them.
 */
export const AUDIT_REQUIRED: Readonly<Record<AuditVerb, readonly string[]>> = {
  schedule: ['Title', 'AuditTemplate', 'BusinessUnit', 'AuditKind', 'DueOn', 'AuditorUPN', 'AuditorIndependent'],
  edit: ['Title', 'AuditTemplate', 'BusinessUnit', 'AuditKind', 'DueOn', 'AuditorUPN', 'AuditorIndependent'],
  start: [],
  complete: ['CompletedOn', 'FindingsLink'],
  approve: ['ScorePct', 'Explanation'],
  followUp: [],
};

/** §3.4.1: the `LogKind` rows the AUD writer adds on this list (G-212 `Review`, G-208 `AuditMeeting`), from the map. */
export const AUDIT_ROW_KINDS: readonly string[] = Object.entries(LOG_KIND_MAP)
  .filter(([, use]) => use.registers.includes('internalAudits') && use.writers.includes('AUD'))
  .map(([kind]) => kind);

// Load-time checks: the stages and verbs name only this list's columns.
for (const cols of [...Object.values(AUDIT_STAGES), ...Object.values(AUDIT_VERB_INPUTS), ...Object.values(AUDIT_REQUIRED)]) {
  for (const c of cols) if (!BY_NAME.has(c)) throw new Error(`internalAudits: ${c} is not an Internal Audits column`);
}

// ---------------------------------------------------------------------------
// The carry-in (FIELDSET §4.1 CAR, §6.7; carry_in_internal_audits.py; 12.1-12.2)
// ---------------------------------------------------------------------------

/**
 * The carry-in's column split, copied from `carry_in_internal_audits.py` (`CARRIED`, `NEVER_WRITTEN`): the re-run after
 * 30 Nov (a Phase 16 step) makes Centro's value win on every `owned` column, except a person it could not resolve.
 * So until then a dashboard write to an owned column of a carried row (AUD-1…11) would be silently undone:
 * - `editable`: the two never-written schedule columns (12.1-12.2: "edit only `AuditKind` / `AuditorIndependent`");
 * - `defaultable`: the person columns the carry-in may have written as the runner's UPN (D-12.1-12.1-9), which a
 *   re-run never overwrites; editable on a row only where its carry-in trail row says so (`carriedDefaults`);
 * - `cutoverDay`: the Melbourne day the guard lifts (the re-run runs after close of business on 30 Nov).
 */
export interface CarryInSplit {
  readonly owned: readonly string[];
  readonly neverWritten: readonly string[];
  readonly editable: readonly string[];
  readonly defaultable: readonly string[];
  readonly cutoverDay: string;
}

export const CARRY_IN: CarryInSplit = {
  owned: [
    'Title', 'AuditTemplate', 'TemplateVersion', 'RecommendedFrequency', 'BusinessUnit', 'DueOn', 'AuditorUPN',
    'AuditStatus', 'ScorePct', 'Explanation', 'Recommendations', 'ApprovedOn', 'ApprovedByUPN', 'CentroRef',
  ],
  neverWritten: ['AuditKind', 'AuditorIndependent', 'CompletedOn', 'FindingsLink', 'Attachments'],
  editable: ['AuditKind', 'AuditorIndependent'],
  defaultable: ['AuditorUPN', 'ApprovedByUPN'],
  cutoverDay: '2026-12-01',
};

/** A row the carry-in wrote (it holds a `CentroRef`). */
export function isCarried(fields: Readonly<Record<string, unknown>>): boolean {
  const v = fields.CentroRef;
  return typeof v === 'string' && v.trim() !== '';
}

const CARRY_IN_NOTE = /^Carried in from Centro\b/;
const DEFAULTED = /\b([A-Za-z]+UPN(?:,\s*[A-Za-z]+UPN)*) defaulted to the runner\b/;

/**
 * The person columns a carried row's carry-in trail row says were defaulted to the runner (the script's own
 * sentence: "<cols> defaulted to the runner: Centro's person did not resolve …"). Only a `StateChange` row whose
 * Note starts "Carried in from Centro" counts; anything else (a person's Note) is ignored. Fails closed: no such row,
 * no defaulted column.
 */
export function carriedDefaults(rows: ReadonlyArray<{ entryType: string | null; note: string | null }>): Set<string> {
  const out = new Set<string>();
  for (const r of rows) {
    if (r.entryType !== 'StateChange' || !r.note || !CARRY_IN_NOTE.test(r.note)) continue;
    const m = DEFAULTED.exec(r.note);
    if (!m) continue;
    for (const col of m[1].split(/,\s*/)) if (CARRY_IN.defaultable.includes(col)) out.add(col);
  }
  return out;
}
